<template>
    <v-layout justify-center>
        <v-responsive max-width="1280" width="100%" height="100%" class="px-3 mx-n3">
            <list-heading title="알림 발송이력" />

            <v-card outlined class="mb-3">
                <v-card-text>
                    <v-row align="center" class="mx-n2">
                        <v-col cols="12" md="4" class="pa-2">
                            <v-select v-model="query.event" :items="eventItems" label="알림 종류" item-text="text" item-value="value" clearable hide-details outlined dense @change="search$" />
                        </v-col>
                        <v-col cols="6" md="2" class="pa-2">
                            <v-select v-model="query.success" :items="successItems" label="발송 결과" item-text="text" item-value="value" clearable hide-details outlined dense @change="search$" />
                        </v-col>
                        <v-col cols="" class="pa-2">
                            <v-text-field v-model="query.searchValue" label="수신자 이름/번호 검색" clearable hide-details outlined dense @keydown.enter="search$" @click:clear="search$" />
                        </v-col>
                        <v-col cols="auto" class="pa-2">
                            <v-btn color="primary" @click="search$">검색</v-btn>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <v-data-table v-bind="{ loading, headers, items }" :expanded.sync="expanded" item-key="_id" show-expand single-expand disable-sort disable-pagination hide-default-footer class="v-sheet--outlined" :items-per-page="-1">
                <template #[`item.event`]="{ value }"> {{ eventLabels[value] || value }} </template>
                <template #[`item.channel`]="{ value }"> {{ channelLabels[value] || value }} </template>
                <template #[`item.createdAt`]="{ value }"> {{ value ? value.toDateTime() : "-" }} </template>
                <template #[`item.success`]="{ value }">
                    <v-chip small label :color="value ? 'green lighten-5' : 'red lighten-5'" :text-color="value ? 'green' : 'red'">{{ value ? "성공" : "실패" }}</v-chip>
                </template>
                <template #[`item.note`]="{ item }">
                    <span v-if="item.success" class="grey--text">{{ item.receiptNum || "-" }}</span>
                    <span v-else class="red--text caption">{{ item.error || "-" }}</span>
                </template>
                <template #expanded-item="{ headers, item }">
                    <td :colspan="headers.length" class="py-3 grey lighten-5">
                        <div class="caption grey--text mb-1">발송 내용</div>
                        <div class="white-space-pre-line body-2">{{ item.message || "-" }}</div>
                        <div v-if="!item.success && item.error" class="red--text caption mt-2">실패 사유: {{ item.error }}</div>
                    </td>
                </template>
            </v-data-table>
            <v-pagination :value="page" :length="pageCount" :total-visible="11" color="primary" class="mt-2" @input="(page) => $router.push({ query: { ...$route.query, page } })"></v-pagination>
        </v-responsive>
    </v-layout>
</template>

<script>
import api from "@/api";

import ListHeading from "@/components/console/dumb/list-heading.vue";

const eventLabels = {
    "member-approved": "회원 승인 안내 (가입자)",
    "contract-issued": "계약서 발행 안내 (가입자)",
    "contract-reminder": "계약서 미진행 리마인드 (가입자)",
    "admin-member-registered": "신규 회원가입 (관리자)",
    "admin-order-basic": "단건배송 주문 (관리자)",
    "admin-order-regular": "정기배송 주문 (관리자)",
    "admin-order-stock": "재고주문 (관리자)",
    "admin-order-stout": "재고 발송 주문 (관리자)",
};
const channelLabels = { kakao: "알림톡", lms: "LMS", sms: "SMS" };

const headers = [
    { divider: true, width: +70, text: "#", value: "index", align: "center" },
    { divider: true, width: 160, text: "발송일시", value: "createdAt" },
    { divider: true, width: 190, text: "알림 종류", value: "event" },
    { divider: true, width: +88, text: "채널", value: "channel", align: "center" },
    { divider: true, width: 110, text: "수신자", value: "to.name" },
    { divider: true, width: 140, text: "수신번호", value: "to.phone" },
    { divider: true, width: +90, text: "결과", value: "success", align: "center" },
    { divider: true, text: "접수번호 / 실패사유", value: "note" },
    { text: "", value: "data-table-expand" },
];

export default {
    components: {
        ListHeading,
    },
    data() {
        return {
            notificationLogs: [],
            expanded: [],

            limit: 20,
            summary: { totalCount: 0 },

            query: {
                event: this.$route.query.event || null,
                success: this.$route.query.success != undefined ? this.$route.query.success : null,
                searchValue: this.$route.query.searchValue || null,
            },

            eventLabels,
            channelLabels,
            eventItems: Object.entries(eventLabels).map(([value, text]) => ({ value, text })),
            successItems: [
                { value: "true", text: "성공" },
                { value: "false", text: "실패" },
            ],
            headers,
            loading: false,
        };
    },
    computed: {
        items() {
            return this.notificationLogs;
        },
        page() {
            return +(this.$route.query.page || "1");
        },
        skip() {
            return (this.page - 1) * this.limit;
        },
        pageCount() {
            return Math.ceil(this.summary.totalCount / this.limit) || 1;
        },
        params() {
            return { ...this.$route.query };
        },
    },
    mounted() {
        this.search();
    },
    watch: {
        params() {
            this.sync();
            this.search();
        },
    },
    methods: {
        // 필터 변경 시 쿼리에 반영(페이지 1로 초기화) → watch(params)에서 search 실행
        search$() {
            const query = { ...this.$route.query, ...this.query, page: 1 };
            Object.keys(query).forEach((key) => {
                if (query[key] === null || query[key] === undefined || query[key] === "") delete query[key];
            });
            this.$router.push({ query }).catch(() => {});
        },
        sync() {
            this.query = {
                event: this.$route.query.event || null,
                success: this.$route.query.success != undefined ? this.$route.query.success : null,
                searchValue: this.$route.query.searchValue || null,
            };
        },
        async search() {
            this.loading = true;
            try {
                const { skip, limit, params } = this;
                const { summary, notificationLogs } = await api.console.notificationLogs.gets({
                    headers: { skip, limit },
                    params,
                });

                this.summary = summary;
                this.notificationLogs = notificationLogs.map((item, index) => ({ ...item, index: summary.totalCount - (this.page - 1) * this.limit - index }));
            } catch (error) {
                this.$handleError(error);
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
::v-deep {
    .white-space-pre-line {
        white-space: pre-line;
    }
    .v-pagination button {
        box-shadow: none !important;
        border: thin solid rgba(0, 0, 0, 0.12);
    }
}
</style>
